import React from "react";
import { Card, CardContent, Typography, Button } from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Customer } from "../../../validation/customer.validation";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import { useNavigate } from "react-router-dom";
import {ROLES} from "../../../constants/general.constant";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  customers: Customer[];
  onDelete: (emp: Customer) => void;
  onEdit: (emp: Customer) => void;
  onBlock: (emp: Customer) => void;
  user?: any
}

const CustomerCardView: React.FC<Props> = ({ customers, onDelete, onEdit, onBlock, user }) => {
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null>(null);

  const handleDeleteConfirm = () => {
    if (selectedCustomer) {
      onDelete(selectedCustomer);
    }
    setOpenDeleteDialog(false);
    setSelectedCustomer(null);
  };

  const handleStatusConfirm = () => {
    if (selectedCustomer) {
      onBlock(selectedCustomer);
    }
    setOpenStatusDialog(false);
    setSelectedCustomer(null);
  };

  const openDeactivateDialog = (emp: Customer) => {
    setSelectedCustomer(emp);
    setOpenStatusDialog(true);
  };

  const openDeleteDialogHandler = (emp: Customer) => {
    setSelectedCustomer(emp);
    setOpenDeleteDialog(true);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 pb-6">
        {customers.map((customer, index) => {
          const isActive = customer.isActive;
          return (
            <Card key={index} className="rounded-2xl shadow border border-gray-200">
              <CardContent className={`flex flex-col items-center text-center px-4 py-6 gap-4 ${!isActive ? "bg-gray-200" : ""}`}>
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center shadow">
                  <PersonIcon className="text-gray-400 text-6xl" />
                </div>

                <Typography variant="h6" className="font-semibold text-gray-900">
                  {`${(customer as any).firstName || ''} ${(customer as any).middleName || ''} ${(customer as any).lastName || ''}`.trim() || (customer as any).name || "-"}
                </Typography>

                <div className="text-sm text-gray-700 space-y-2 text-left w-full">
                  <div className="flex items-center gap-2">
                    <EmailIcon fontSize="small" />
                    <span>
                      <strong>Email:</strong> {customer.email || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon fontSize="small" />
                    <span>
                      <strong>Phone:</strong> {customer.phone ? formatPhoneNumber(customer.phone) : "-"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-4">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    color="info"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => {
                      if (user?.role === ROLES.CO_OWNER) {
                        navigate(`/co-owner/customers/${customer.id}`);
                      } else if (user.role === ROLES.EMPLOYEE) {
                        navigate(`/employee/customers/${customer.id}`);
                      } else {
                        navigate(`/customers/${customer.id}`);
                      }
                    }}
                  >
                    View
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onEdit(customer)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isActive ? <BlockIcon /> : <EnableIcon />}
                    color={isActive ? "warning" : "success"}
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeactivateDialog(customer)}
                  >
                    {isActive ? "Deactivate" : "Enable"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeleteDialogHandler(customer)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={(selectedCustomer?.firstName + " " + selectedCustomer?.lastName) || ""}
      />

      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedCustomer?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={(selectedCustomer?.firstName + " " + selectedCustomer?.lastName)}
      />
    </>
  );
};

export default CustomerCardView;
